/**
 * Copy-context payload helpers
 *
 * Builds the JSON payload that powers the "Copy context as JSON" header
 * action on Collection and smart-collection tabs. The output is the
 * paste-elsewhere shape — LLM prompts, doc context, a ticket draft —
 * so it intentionally drops fields that only matter to ingest /
 * pipeline / storage layers:
 *
 *   - File references and binary metadata (thumbnails, exif, origin
 *     device) — internal storage paths and EXIF blobs that don't help
 *     an outside reader.
 *   - Ingest / pipeline bookkeeping (processingStatus, ingestorVersion,
 *     lineageId, parentBlockId, sourceActivityId).
 *   - The full Tiptap body of `note` blocks — verbose JSON tree that
 *     buries the readable text; title + noteType + summary capture the
 *     essence.
 *
 * Per-type `content` summarization picks variant-relevant fields. The
 * link variant includes only url / domain / subtype because the deep
 * nested payload (OG card chrome, tweet author rows, etc.) duplicates
 * what `title`/`summary` already cover and bloats the JSON.
 *
 * Sub-collection recursion: the parent collection payload nests its
 * direct child collections (Stacks / subcollections) as a recursive
 * `childCollections` array. Each child is itself a full node — its own
 * metadata + `blocks` + nested `childCollections` — so the entire
 * subtree round-trips into the paste. This lives in `fetchTreeNode`,
 * which the caller drives via `@tanstack/react-query`'s `QueryClient`
 * so repeated copies within the cache window reuse network results.
 *
 * No block cap: the action serializes every reachable block. The
 * `MAX_BLOCKS_IN_COPY_CONTEXT` guardrail that used to truncate at 500
 * was removed by request — the caller accepts the clipboard / context
 * size trade-off for completeness on large trees.
 *
 * Shared between `app-screen.tsx` (regular Collection header) and
 * `smart-collection-tab-content.tsx` (transient assistant-query tabs)
 * so the two surfaces produce identical-shape payloads for the same
 * block. Smart collections don't have a child-collection axis (they're
 * a flat assistant-curated id list) so only the block summarizer
 * applies there.
 */
import { getCollectionDetails, listCollectionChildren } from "@/api/query-api";
/**
 * Page size for the per-collection block fetch. Pinned to the server's
 * `BlockFilterSchema.limit.max(250)` ceiling — going higher fails Zod
 * validation inside `BlockEntity.list` and the
 * `/collection/:id/details` route surfaces the ValidationError. Using
 * the ceiling minimises round-trips on a deep copy: a 1000-block
 * collection now clears in 4 requests instead of 10.
 */
const COPY_CONTEXT_PAGE_SIZE = 250;
/**
 * Tanstack Query staleTime for copy-context fetches. Same-window
 * repeated copies (e.g. user mashes the button twice) reuse the
 * cached pages; longer than this and the next copy refetches so we
 * pick up any membership / metadata changes made elsewhere.
 */
const COPY_CONTEXT_STALE_TIME_MS = 30_000;
/**
 * Reduce a block envelope to the textual + structural fields that are
 * useful as paste-elsewhere context (LLM chat, doc, ticket). See the
 * module doc for the rationale on what's dropped.
 */
export function summarizeBlockForContext(envelope) {
    const b = envelope.block;
    const out = {
        id: b.id,
        type: b.type,
        capturedAt: b.capturedAt,
    };
    if (b.title)
        out.title = b.title;
    if (b.summary)
        out.summary = b.summary;
    if (b.context)
        out.context = b.context;
    if (b.slug)
        out.slug = b.slug;
    if (b.metadata.source) {
        out.source = {
            url: b.metadata.source.url,
            domain: b.metadata.source.domain,
        };
    }
    if (b.metadata.geolocation?.address) {
        out.location = b.metadata.geolocation.address;
    }
    const content = summarizeBlockContent(b);
    if (content !== undefined)
        out.content = content;
    return out;
}
function summarizeBlockContent(block) {
    // `Block` is defined as `Omit<DiscriminatedUnion, …> & { … }` which
    // breaks TS's automatic content-narrowing on `block.type`. We follow
    // the codebase convention (see `block-view.tsx`, `block-styles.ts`,
    // etc.) and cast `block.content` to the matching variant by hand.
    switch (block.type) {
        case "text": {
            const text = block.content;
            const c = {};
            if (text.title)
                c.title = text.title;
            if (text.text)
                c.text = text.text;
            return Object.keys(c).length > 0 ? c : undefined;
        }
        case "note": {
            const note = block.content;
            const c = { noteType: note.noteType };
            if (note.title)
                c.title = note.title;
            if (note.journalDate)
                c.journalDate = note.journalDate;
            return c;
        }
        case "link": {
            const link = block.content;
            const c = { subtype: link.subtype };
            if (link.url)
                c.url = link.url;
            if (link.domain)
                c.domain = link.domain;
            return c;
        }
        case "image": {
            const img = block.content;
            const c = {};
            if (img.width !== undefined)
                c.width = img.width;
            if (img.height !== undefined)
                c.height = img.height;
            if (img.format)
                c.format = img.format;
            return Object.keys(c).length > 0 ? c : undefined;
        }
        case "audio": {
            const audio = block.content;
            const c = {};
            if (audio.duration !== undefined)
                c.duration = audio.duration;
            if (audio.format)
                c.format = audio.format;
            return Object.keys(c).length > 0 ? c : undefined;
        }
        case "video": {
            const video = block.content;
            const c = {};
            if (video.duration !== undefined)
                c.duration = video.duration;
            if (video.format)
                c.format = video.format;
            return Object.keys(c).length > 0 ? c : undefined;
        }
        case "document": {
            const doc = block.content;
            const c = {};
            if (doc.format)
                c.format = doc.format;
            if (doc.pageCount !== undefined)
                c.pageCount = doc.pageCount;
            return Object.keys(c).length > 0 ? c : undefined;
        }
        case "email": {
            const email = block.content;
            const c = {
                subject: email.subject,
                from: email.from,
                to: email.to,
                sentAt: email.sentAt,
            };
            if (email.cc)
                c.cc = email.cc;
            if (email.bodyText)
                c.bodyText = email.bodyText;
            return c;
        }
    }
    return undefined;
}
/**
 * Reduce a child-collection list item to the descriptive fields a
 * paste consumer cares about. Stripped: `parentCollectionId` (already
 * implied by the nesting position), ACL fields, publishedAt, the full
 * metadata blob (only emoji is meaningful for downstream prose).
 */
export function summarizeCollectionForContext(item) {
    const out = {
        id: item.id,
        type: item.type,
    };
    if (item.layerIndex !== undefined)
        out.layerIndex = item.layerIndex;
    if (item.title)
        out.title = item.title;
    if (item.summary)
        out.summary = item.summary;
    if (item.metadata.emoji)
        out.emoji = item.metadata.emoji;
    if (item.blockCount !== undefined)
        out.blockCount = item.blockCount;
    if (item.slug)
        out.slug = item.slug;
    return out;
}
/**
 * Fetch every block on `collectionId`, paging through
 * `/collection/:id/details` until the server-reported `count` is
 * exhausted. Each page is cached in the query client so a repeat copy
 * within `COPY_CONTEXT_STALE_TIME_MS` hits cache.
 */
async function fetchAllBlocksForCollection(queryClient, collectionId) {
    const firstPage = await queryClient.fetchQuery({
        queryKey: ["copyContext.details", collectionId, 0, COPY_CONTEXT_PAGE_SIZE],
        queryFn: () => getCollectionDetails(collectionId, { limit: COPY_CONTEXT_PAGE_SIZE, offset: 0 }),
        staleTime: COPY_CONTEXT_STALE_TIME_MS,
    });
    const total = firstPage.count;
    if (total <= firstPage.blocks.length) {
        return firstPage.blocks;
    }
    const offsets = [];
    for (let off = COPY_CONTEXT_PAGE_SIZE; off < total; off += COPY_CONTEXT_PAGE_SIZE) {
        offsets.push(off);
    }
    const restPages = await Promise.all(offsets.map((off) => queryClient.fetchQuery({
        queryKey: ["copyContext.details", collectionId, off, COPY_CONTEXT_PAGE_SIZE],
        queryFn: () => getCollectionDetails(collectionId, { limit: COPY_CONTEXT_PAGE_SIZE, offset: off }),
        staleTime: COPY_CONTEXT_STALE_TIME_MS,
    })));
    return [...firstPage.blocks, ...restPages.flatMap((p) => p.blocks)];
}
/**
 * Fetch a collection's direct children, cached in the query client.
 */
async function fetchChildrenForCollection(queryClient, collectionId) {
    return queryClient.fetchQuery({
        queryKey: ["copyContext.children", collectionId],
        queryFn: () => listCollectionChildren(collectionId),
        staleTime: COPY_CONTEXT_STALE_TIME_MS,
    });
}
/**
 * Walk a collection subtree, emitting a nested
 * `{ collection, blocks, childCollections }` payload and the running
 * total counts. The `visited` set is cheap insurance against an
 * accidental cycle — the layer model precludes one (collections form
 * a strict tree), but a defensive bound is one Set lookup per node.
 *
 * Children are fetched in parallel via `Promise.all` so a wide tree
 * (many subcollections under one parent) doesn't serialize the round
 * trips; pages within a single collection are parallelized after the
 * first page lands (we need its `count` to compute the offsets).
 */
export async function fetchCollectionContextTree(queryClient, collectionId, collectionMeta, counts, visited = new Set()) {
    if (visited.has(collectionId)) {
        // Defensive: surface the cycle as an empty leaf rather than
        // recursing forever. The layer model shouldn't allow this.
        return {
            collection: summarizeCollectionForContext(collectionMeta),
            blocks: [],
            childCollections: [],
        };
    }
    visited.add(collectionId);
    const [blocks, children] = await Promise.all([
        fetchAllBlocksForCollection(queryClient, collectionId),
        fetchChildrenForCollection(queryClient, collectionId),
    ]);
    counts.blocks += blocks.length;
    counts.collections += children.length;
    const childNodes = await Promise.all(children.map((child) => fetchCollectionContextTree(queryClient, child.id, child, counts, visited)));
    return {
        collection: summarizeCollectionForContext(collectionMeta),
        blocks: blocks.map(summarizeBlockForContext),
        childCollections: childNodes,
    };
}
